import { z } from 'zod';

import { fnv1a, truncate, type Json } from '@vestigas/utils';

/**
 * Structural mirror of `ResponseValidationProps` in
 * `packages/logger/src/responseValidation.ts` — deliberately re-declared rather
 * than imported, so `packages/data` gains no dependency on `@vestigas/logger`.
 * Declared once here and reused by the ky client's `Logger` type.
 */
export type ResponseValidationProps = {
  digest: string;
  entityId?: string;
  issues: unknown[];
  key: string;
  message: string;
  totalIssues: number;
};

/** Structural type for the injected logger. */
export type ResponseValidationLogger = {
  responseValidation: (props: ResponseValidationProps) => void;
};

// Key-name tests run against `segmentWords`, which lowercases and splits
// camelCase, so these are whole-word and case-flag-free. Substring matching was
// the bug: `vat` fires inside `activatedAt` and `deactivatedAt`, `tax` inside
// `taxonomy`, masking ordinary timestamps into uselessness.
const REDACT =
  /\b(password|secret|token|credential|credentials|auth|authorization|api key|cookie|session|private key|pin|otp)\b/;
const MASK = /\b(mail|email|phone|mobile|iban|bic|vat|tax|ust)\b/;
// Credential-shaped VALUES that slip past the key-name check. Tested against
// the RAW value before truncation — a marker past character 200 would
// otherwise be cut off and the value would ship.
const VALUE_SCRUB =
  /\b(bearer|basic)\s|eyJ[A-Za-z0-9_-]{10,}|-----BEGIN|^ssh-(rsa|ed25519|dss)\s/i;
const UUID_SEGMENT = /^[\da-f]{8}(?:-[\da-f]{4}){3}-[\da-f]{12}/i;
// Record keys shaped like `DN-2026-000001`, and any long digit run.
const PREFIXED_ID_SEGMENT = /^[A-Za-z]{2,}[-_]\d/;
const DIGIT_RUN_SEGMENT = /\d{6,}/;
const MAX_REPORTED_ISSUES = 10;
const MAX_REPORTED_KEYS = 20;
const MAX_SEGMENT_LENGTH = 40;
const MAX_VALUE_LENGTH = 200;

// 'vatId' → 'vat id', 'activatedAt' → 'activated at'. Lets REDACT/MASK match
// whole words instead of substrings.
//
// The acronym pass runs first and is what makes an ACRONYM-cased name match at
// all: the lower-to-upper rule alone never fires inside a run of capitals, so
// 'VATId' would collapse to 'vatid' and slip past `\bvat\b` — the value would
// then ship in the report. It splits a capital run from the capitalised word
// that follows ('VATId' → 'VAT Id', 'APIKey' → 'API Key'), which leaves
// all-caps enum values and 'HTTPError' untouched. Names with no case boundary
// left to find ('vatid', 'apikey') stay unmatched; VALUE_SCRUB is their only
// backstop.
function segmentWords(segment: PropertyKey): string {
  return String(segment)
    .replaceAll(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replaceAll(/([a-z\d])([A-Z])/g, '$1 $2')
    .replaceAll(/[^a-zA-Z\d]+/g, ' ')
    .toLowerCase();
}

// A path segment is either a FIELD NAME (from the schema — stable, the thing
// to identify) or a RECORD IDENTIFIER (array index, UUID or other `z.record`
// key — varies per record, and is PAYLOAD rather than schema). Field names are
// safe to report and must stay in the digest; record identifiers are neither.
function isRecordIdentifier(segment: PropertyKey): boolean {
  const text = String(segment);

  return (
    typeof segment === 'number' ||
    /^\d+$/.test(text) ||
    UUID_SEGMENT.test(text) ||
    PREFIXED_ID_SEGMENT.test(text) ||
    DIGIT_RUN_SEGMENT.test(text) ||
    // A schema field name never contains '@' or whitespace; a record keyed by
    // an email address or a person's name does.
    text.includes('@') ||
    /\s/.test(text) ||
    text.length > MAX_SEGMENT_LENGTH
  );
}

// For the digest: every record identifier collapses, so the same failure shape
// hashes identically across records.
function normalizeSegment(segment: PropertyKey): string {
  return isRecordIdentifier(segment) ? '*' : String(segment);
}

// For the reported path: array indices are kept (which row failed is real
// diagnostic value and is not payload), but string record keys are collapsed —
// they are the document's data, and nothing else suppresses them, not even
// `isSensitive: true` / `isResponseSensitive: true`.
function reportedPath(path: readonly PropertyKey[]): PropertyKey[] {
  return path.map((segment) =>
    typeof segment === 'number' ? segment : normalizeSegment(segment),
  );
}

// Opaque failure-shape id: fnv1a of the sorted unique normalized `path:code`
// pairs. A dedup key and a `shape` tag for log.responseValidation — it does
// NOT drive Sentry grouping (grouping is per endpoint via `key`).
function digestOf(issues: readonly z.core.$ZodIssue[]): string {
  const pairs = issues.map(
    (issue) => `${issue.path.map(normalizeSegment).join('.')}:${issue.code}`,
  );

  return fnv1a([...new Set(pairs)].toSorted().join('|'));
}

// 'anna.schmidt@bau.de-INVALID ' → 'an***@***.de, len=28' — keeps a short
// prefix, the format tail and the ORIGINAL length (so trailing-whitespace and
// wrong-TLD signals survive) while dropping the identifier itself. Short values
// get no prefix: two of three characters is not a mask.
function maskValue(value: string): string {
  const prefix = value.length <= 5 ? '' : value.slice(0, 2);
  const lengthSuffix = `, len=${value.length}`;
  const atIndex = value.indexOf('@');

  if (atIndex === -1) {
    return `${prefix}***${lengthSuffix}`;
  }

  const lastDotIndex = value.lastIndexOf('.');
  const tld =
    lastDotIndex > atIndex
      ? (/^[a-z]+/i.exec(value.slice(lastDotIndex + 1))?.[0] ?? '')
      : '';

  return `${prefix}***@***${tld === '' ? '' : `.${tld}`}${lengthSuffix}`;
}

function sanitizeInput(input: unknown, path: readonly PropertyKey[]): unknown {
  // With sensitive handling (no reportInput) the issue carries no input at all.
  // Also fires for a missing required field in non-sensitive mode, where zod
  // attaches the key with an undefined value.
  if (input === undefined) {
    return undefined;
  }

  // ANY segment, not just the leaf — on ancestor-level failures the last path
  // segment is an array index, so a leaf-only check never fires while `input`
  // is the entire object.
  if (path.some((segment) => REDACT.test(segmentWords(segment)))) {
    return '[redacted]';
  }

  // Arrays: the length is the diagnostic value. Reporting index names instead
  // says nothing and hides how many rows arrived.
  if (Array.isArray(input)) {
    return { length: input.length, type: 'array' };
  }

  // Other non-primitives: shape only. Key names are usually the diagnostic
  // value, but a `z.record` key is payload — so they get normalized too.
  if (input !== null && typeof input === 'object') {
    const keys = Object.keys(input);

    return {
      keyCount: keys.length,
      keys: keys.slice(0, MAX_REPORTED_KEYS).map(normalizeSegment),
      type: 'object',
    };
  }

  const text = String(input as boolean | number | string | null);

  // Scrub before truncating and before masking: a credential value under an
  // otherwise-innocent field name must not survive as a masked descriptor.
  if (VALUE_SCRUB.test(text)) {
    return '[redacted]';
  }

  // Masking is keyed on the FIELD NAME, not the value's JS type. Gating it on
  // `typeof input === 'string'` missed the modal case: this helper only runs
  // because the type drifted, so a `vatId`/`phone`/`iban` arriving as a number
  // is exactly when the mask is needed.
  if (path.some((segment) => MASK.test(segmentWords(segment)))) {
    return maskValue(text);
  }

  return truncate(text, MAX_VALUE_LENGTH);
}

// zod builds `unrecognized_keys` messages out of the payload's own key names,
// which are record data for a `z.record`. Every other code's message is
// schema-derived. VALUE_SCRUB is a backstop for custom `error` functions that
// interpolate the failing value (none exist in this repo today).
function sanitizeMessage(issue: z.core.$ZodIssue): string {
  if (issue.code === 'unrecognized_keys') {
    return `Unrecognized keys: ${issue.keys.map(normalizeSegment).join(', ')}`;
  }

  return VALUE_SCRUB.test(issue.message) ? '[redacted]' : issue.message;
}

// Explicit projection — NEVER a spread: `invalid_union` issues carry nested
// `errors: issue[][]` whose members each have their own `input`, so a spread
// would rewrite only the top-level `input` and ship every nested value
// verbatim. The issue COUNT is capped too: a long list with one failing field
// per row would otherwise reassemble a cross-section of the document.
function sanitize(issues: readonly z.core.$ZodIssue[]) {
  return issues.slice(0, MAX_REPORTED_ISSUES).map((issue) => ({
    code: issue.code,
    expected: 'expected' in issue ? issue.expected : undefined,
    input: sanitizeInput(issue.input, issue.path),
    message: sanitizeMessage(issue),
    path: reportedPath(issue.path),
  }));
}

/**
 * Turns raw zod issues into the reportable payload: sanitized issues, the
 * shape digest, and the pre-truncation count.
 *
 * ONE implementation, two entry points — the ky client's `validate.response`
 * tap calls this directly, and `safeParseResponse` below calls it for the
 * escape-hatch path. Sanitization is unconditional in both: it runs whether or
 * not the caller asked for sensitive handling.
 */
export function buildResponseValidationReport(
  issues: readonly z.core.$ZodIssue[],
): Pick<ResponseValidationProps, 'digest' | 'issues' | 'totalIssues'> {
  return {
    digest: digestOf(issues),
    issues: sanitize(issues),
    totalIssues: issues.length,
  };
}

/**
 * Builds the shared response-parse helper bound to an app's logger instance —
 * implementation defined once here, each app instantiates it in
 * `src/data/utils.ts` with its own `log` (same pattern as `createKyInstance`).
 *
 * On validation failure the helper reports via `log.responseValidation`
 * (sanitized issues + shape digest) and returns the RAW response unchanged:
 * drift must never break the UI.
 *
 * `isSensitive: true` parses without `reportInput`, so no failing values are
 * captured — for endpoints whose whole response is delicate (e.g. FTP poller
 * secrets). It replaces the former `safeParseSensitiveResponse`. Note that
 * `path` and `message` are sanitized for every call, sensitive or not: neither
 * is suppressed by `reportInput`, and both can carry record data.
 *
 * PREFER `validate: { response: schema }` on the ky request — this helper is
 * the escape hatch for schemas whose output does not survive a JSON round trip
 * (`z.coerce.date()` → `Date`), which the client's body substitution requires.
 */
export function createSafeParseResponse({
  log,
}: {
  log: ResponseValidationLogger;
}) {
  return function safeParseResponse<TSchema extends z.ZodTypeAny>({
    entityId,
    isSensitive = false,
    key,
    message,
    response,
    schema,
  }: {
    /** Subject of the response for single-resource endpoints; omit for lists and bulk operations. */
    entityId?: string;
    /** Skip `reportInput` so no failing values are captured at all. */
    isSensitive?: boolean;
    /** Stable per-endpoint key, kebab-case. Drives Sentry grouping and dedup. */
    key: string;
    message: string;
    response: Json;
    schema: TSchema;
  }): z.infer<TSchema> | Json {
    const validated = schema.safeParse(response, { reportInput: !isSensitive });

    if (!validated.success) {
      log.responseValidation({
        ...buildResponseValidationReport(validated.error.issues),
        entityId,
        key,
        message,
      });

      // Passthrough on failure — drift must never break the UI.
      return response;
    }

    return validated.data;
  };
}
